import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getAuthToken } from '../../utils/auth';

const PreSalesOrderCreationModal = ({
  salesOrderData,
  onConfirm,
  onCancel,
  onClose,
  token,
  workflowContext,
  runId: propRunId,
  workflowId: propWorkflowId
}) => {
  // State
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedItems, setSelectedItems] = useState({});
  const [selectedTaxCode, setSelectedTaxCode] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [allCustomers, setAllCustomers] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [itemSearchTerms, setItemSearchTerms] = useState({});
  const [loadingAllCustomers, setLoadingAllCustomers] = useState(false);
  const [loadingAllItems, setLoadingAllItems] = useState(false);

  // Creation status
  const [creationStatus, setCreationStatus] = useState(null);
  const [creationMessage, setCreationMessage] = useState('');
  const [netSuiteUrl, setNetSuiteUrl] = useState('');

  // Minimize state
  const [isMinimized, setIsMinimized] = useState(false);

  // AI Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const chatMessagesEndRef = useRef(null);

  // Modified sale data from AI chat
  const [modifiedSaleData, setModifiedSaleData] = useState(null);

  // Get current data - use modifiedSaleData if available (from AI chat)
  const currentData = useMemo(() => {
    // If AI chat has modified the data, use that
    if (modifiedSaleData) return modifiedSaleData;

    if (!salesOrderData) return null;

    // Handle nested data structures
    if (salesOrderData.sale_data) return salesOrderData.sale_data;
    if (salesOrderData.sales_order_data) return salesOrderData.sales_order_data;
    return salesOrderData;
  }, [salesOrderData, modifiedSaleData]);

  // Scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatMessagesEndRef.current) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Handle AI chat message submission
  const handleChatSubmit = async (e) => {
    e?.preventDefault();

    const userMessage = chatInput.trim();
    if (!userMessage || isChatLoading) return;

    // Add user message to chat
    const userChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setChatMessages(prev => [...prev, userChatMessage]);
    setChatInput('');
    setIsChatLoading(true);

    try {
      const authToken = getAuthToken() || token;
      if (!authToken) {
        throw new Error('No authentication token available. Please login again.');
      }

      // Prepare sale data for API - remove large arrays to reduce payload
      const optimizedData = { ...(modifiedSaleData || currentData) };
      delete optimizedData.all_netsuite_items;
      delete optimizedData.all_netsuite_customers;
      delete optimizedData.all_netsuite_classes;
      delete optimizedData.all_netsuite_locations;
      delete optimizedData.all_netsuite_tax_codes;

      console.log('[SALE AI CHAT] Sending POST request to /api/sale/modify-sale-chat');
      console.log('[SALE AI CHAT] Request payload:', {
        user_message: userMessage,
        has_sale_data: !!optimizedData,
        line_items_count: optimizedData?.line_items?.length || optimizedData?.mapped_items?.length || 0
      });

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/sale/modify-sale-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          user_message: userMessage,
          current_sale_data: optimizedData,
          conversation_history: chatMessages,
          run_id: propRunId,
          workflow_id: propWorkflowId
        })
      });

      console.log('[SALE AI CHAT] Response received, status:', response.status);

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('[SALE AI CHAT] Non-JSON response received:', textResponse.substring(0, 200));
        throw new Error(`Server returned non-JSON response (status: ${response.status})`);
      }

      const data = await response.json();

      if (data.success) {
        // Add AI response to chat
        const aiMessage = {
          role: 'assistant',
          content: data.ai_response,
          timestamp: new Date().toISOString()
        };
        setChatMessages(prev => [...prev, aiMessage]);

        // Update sale data if modified
        if (data.modified_sale_data) {
          console.log('[SALE AI CHAT] Updating sale data from AI response');
          setModifiedSaleData(data.modified_sale_data);

          // Update selected customer if changed
          if (data.modified_sale_data.customer_id) {
            setSelectedCustomer(data.modified_sale_data.customer_id);
          }

          // Update selected items if line items changed
          const lineItems = data.modified_sale_data.line_items || data.modified_sale_data.mapped_items || [];
          const itemSelections = {};
          lineItems.forEach((item, index) => {
            if (item.netsuite_item_id) {
              itemSelections[index] = item.netsuite_item_id;
            }
          });
          if (Object.keys(itemSelections).length > 0) {
            setSelectedItems(prev => ({ ...prev, ...itemSelections }));
          }
        }
      } else {
        // Add error message to chat
        const errorMessage = {
          role: 'assistant',
          content: `Sorry, I encountered an error: ${data.error || 'Unknown error'}`,
          isError: true,
          timestamp: new Date().toISOString()
        };
        setChatMessages(prev => [...prev, errorMessage]);
      }
    } catch (err) {
      console.error('[SALE AI CHAT] Error:', err);
      const errorMessage = {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${err.message}`,
        isError: true,
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsChatLoading(false);
    }
  };

  // Track if we've initialized item selections (separate from other fields)
  const [hasInitializedItems, setHasInitializedItems] = useState(false);

  // Initialize customer and items from data - ONLY ONCE
  useEffect(() => {
    if (!currentData || hasInitializedItems) return;

    // Set customer
    if (currentData.customer_id) {
      setSelectedCustomer(currentData.customer_id);
    }

    // Set item selections - only on first load
    const lineItems = currentData.line_items || currentData.mapped_items || [];
    const itemSelections = {};
    lineItems.forEach((item, index) => {
      if (item.netsuite_item_id) {
        itemSelections[index] = item.netsuite_item_id;
      }
    });
    setSelectedItems(itemSelections);

    // Mark items as initialized so we don't reset user selections
    setHasInitializedItems(true);
  }, [currentData, hasInitializedItems]);

  // Update location/class/tax when workflowContext loads (can update anytime)
  useEffect(() => {
    if (!workflowContext) return;

    // Set location from workflow context
    const locationId = workflowContext.location_id ||
      workflowContext.get_location_class_ai_step_data?.location_id;
    if (locationId && !selectedLocation) {
      console.log('📍 Setting location from workflowContext:', locationId);
      setSelectedLocation(locationId);
    }

    // Set class from workflow context
    const classId = workflowContext.class_id ||
      workflowContext.get_location_class_ai_step_data?.class_id;
    if (classId && !selectedClass) {
      console.log('🏷️ Setting class from workflowContext:', classId);
      setSelectedClass(classId);
    }

    // Set tax code from workflow context
    const taxCodeId = workflowContext.tax_code_id ||
      workflowContext.identify_tax_code_ai_step_data?.default_tax_code_id;
    if (taxCodeId && !selectedTaxCode) {
      console.log('💰 Setting tax code from workflowContext:', taxCodeId);
      setSelectedTaxCode(taxCodeId);
    }
  }, [workflowContext, selectedLocation, selectedClass, selectedTaxCode]);


  // Format currency
  const formatCurrency = (amount, currencySymbol = '$') => {
    if (amount === null || amount === undefined) return '-';
    return `${currencySymbol}${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Handle load all customers
  const handleLoadAllCustomers = async () => {
    const authToken = token || getAuthToken();
    const effectiveRunId = propRunId || workflowContext?.run_id;
    const subsidiaryId = workflowContext?.subsidiary_id || currentData?.subsidiary_id;

    console.log('🔍 Load All Customers Debug:', {
      token: authToken ? 'present' : 'missing',
      effectiveRunId,
      subsidiaryId
    });

    if (!authToken || !effectiveRunId) {
      console.error('❌ Cannot load customers: missing token or runId');
      alert('Cannot load customers: Missing authentication or workflow ID. Please try reopening the workflow.');
      return;
    }

    setLoadingAllCustomers(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const url = subsidiaryId
        ? `${API_BASE_URL}/api/sale/netsuite-customers-all?run_id=${effectiveRunId}&subsidiary_id=${subsidiaryId}`
        : `${API_BASE_URL}/api/sale/netsuite-customers-all?run_id=${effectiveRunId}`;

      console.log(`👥 Fetching all customers from: ${url}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      const data = await response.json();

      if (data.success && data.customers) {
        console.log(`✅ Loaded ${data.customers.length} customers`);
        setAllCustomers(data.customers);
      } else {
        console.error('❌ Failed to load customers:', data.error || data.detail);
        alert(`Failed to load customers: ${data.error || data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('❌ Error loading customers:', err);
      alert(`Error loading customers: ${err.message}`);
    } finally {
      setLoadingAllCustomers(false);
    }
  };

  // Handle load all items
  const handleLoadAllItems = async () => {
    const authToken = token || getAuthToken();
    const effectiveRunId = propRunId || workflowContext?.run_id;
    const subsidiaryId = workflowContext?.subsidiary_id || currentData?.subsidiary_id;

    console.log('🔍 Load All Items Debug:', {
      token: authToken ? 'present' : 'missing',
      effectiveRunId,
      subsidiaryId
    });

    if (!authToken || !effectiveRunId) {
      console.error('❌ Cannot load items: missing token or runId');
      alert('Cannot load items: Missing authentication or workflow ID. Please try reopening the workflow.');
      return;
    }

    setLoadingAllItems(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const url = subsidiaryId
        ? `${API_BASE_URL}/api/sale/netsuite-items-all?run_id=${effectiveRunId}&subsidiary_id=${subsidiaryId}`
        : `${API_BASE_URL}/api/sale/netsuite-items-all?run_id=${effectiveRunId}`;

      console.log(`📦 Fetching all items from: ${url}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      const data = await response.json();

      if (data.success && data.items) {
        console.log(`✅ Loaded ${data.items.length} items`);
        setAllItems(data.items);
      } else {
        console.error('❌ Failed to load items:', data.error || data.detail);
        alert(`Failed to load items: ${data.error || data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('❌ Error loading items:', err);
      alert(`Error loading items: ${err.message}`);
    } finally {
      setLoadingAllItems(false);
    }
  };

  // Handle create sales order
  const handleCreateSalesOrder = async () => {
    setLoading(true);
    setCreationStatus(null);

    try {
      const authToken = token || getAuthToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';

      // Build line items with full item details from loaded items
      const lineItems = (currentData?.line_items || currentData?.mapped_items || []).map((item, index) => {
        const userSelectedItemId = selectedItems[index];
        let selectedItemDetails = null;

        // If user selected an item from loaded items, get full details
        if (userSelectedItemId && allItems.length > 0) {
          const foundItem = allItems.find(i => String(i.id) === String(userSelectedItemId));
          if (foundItem) {
            console.log(`✅ Found selected item in allItems for line ${index}:`, foundItem);
            selectedItemDetails = {
              netsuite_item_id: foundItem.id,
              item_name_code: foundItem.itemid,
              item_description: foundItem.description || foundItem.displayname,
              item_type: foundItem.itemtype
            };
          }
        }

        return {
          item_id: userSelectedItemId || item.netsuite_item_id,
          // Include full item details if found from loaded items
          ...(selectedItemDetails && {
            netsuite_item_id: selectedItemDetails.netsuite_item_id,
            item_name_code: selectedItemDetails.item_name_code,
            item_description: selectedItemDetails.item_description,
            item_type: selectedItemDetails.item_type
          }),
          quantity: item.quantity || item.po_quantity || 1,
          rate: item.unit_price || item.po_unit_price || 0,
          description: selectedItemDetails?.item_description || item.description || item.item_description || item.po_item_description || '',
          tax_code_id: selectedTaxCode,
          // Preserve delivery date
          rdd: item.rdd || item.delivery_date || item.requested_delivery_date
        };
      });

      // DEBUG: Log the state values before building payload
      console.log('🔍 DEBUG - State values before payload:', {
        selectedLocation,
        selectedClass,
        selectedTaxCode,
        workflowContext_location_id: workflowContext?.location_id,
        workflowContext_class_id: workflowContext?.class_id,
        workflowContext: workflowContext
      });

      // Get full customer details if selected from loaded customers
      let customerDetails = null;
      const customerIdToUse = selectedCustomer || currentData?.customer_id;
      if (customerIdToUse && allCustomers.length > 0) {
        const foundCustomer = allCustomers.find(c => String(c.id) === String(customerIdToUse));
        if (foundCustomer) {
          console.log(`✅ Found selected customer in allCustomers:`, foundCustomer);
          customerDetails = {
            netsuite_customer_id: foundCustomer.id,
            customer_name: foundCustomer.company_name || foundCustomer.companyname,
            entity_id: foundCustomer.entity_id || foundCustomer.entityid,
            email: foundCustomer.email,
            phone: foundCustomer.phone
          };
        }
      }

      // Build sales order payload - use state values with fallback to workflowContext
      const finalLocationId = selectedLocation || workflowContext?.location_id ||
        workflowContext?.get_location_class_ai_step_data?.location_id || currentData?.location_id;
      const finalClassId = selectedClass || workflowContext?.class_id ||
        workflowContext?.get_location_class_ai_step_data?.class_id || currentData?.class_id;
      const finalTaxCodeId = selectedTaxCode || workflowContext?.tax_code_id ||
        workflowContext?.identify_tax_code_ai_step_data?.default_tax_code_id || currentData?.tax_code_id;

      console.log('🎯 Final IDs for payload:', { finalLocationId, finalClassId, finalTaxCodeId });

      const salesOrderPayload = {
        customer_id: customerIdToUse,
        // Include full customer details if found from loaded customers
        ...(customerDetails && {
          netsuite_customer_id: customerDetails.netsuite_customer_id,
          customer_name: customerDetails.customer_name,
          customer_entity_id: customerDetails.entity_id,
          customer_email: customerDetails.email,
          customer_phone: customerDetails.phone
        }),
        subsidiary_id: workflowContext?.subsidiary_id || currentData?.subsidiary_id,
        currency_id: workflowContext?.currency_id || currentData?.currency_id,
        location_id: finalLocationId,
        class_id: finalClassId,
        tax_code_id: finalTaxCodeId,
        trandate: currentData?.sales_date || new Date().toISOString().split('T')[0],
        otherrefnum: currentData?.customer_po_number || '',
        memo: `Created from PO: ${currentData?.customer_po_number || 'N/A'}`,
        line_items: lineItems
      };

      console.log('📦 Sending sales order payload to parent via onConfirm:', salesOrderPayload);

      // Call parent's onConfirm handler with the payload
      // The parent (SaleV2ChatMessages) will use the hook's sendMessage function
      // which properly manages polling state and timestamp tracking
      if (onConfirm) {
        onConfirm(salesOrderPayload);

        // Show processing message briefly before closing
        setCreationStatus('processing');
        setCreationMessage('Sales order creation in progress... Check the chat for updates.');

        // Close modal after a short delay to show the message
        setTimeout(() => {
          if (onClose) {
            onClose();
          }
        }, 1500);
      } else {
        setCreationStatus('error');
        setCreationMessage('Unable to submit - confirmation handler not available');
      }
    } catch (err) {
      console.error('Error creating sales order:', err);
      setCreationStatus('error');
      setCreationMessage(err.message || 'An error occurred while creating the sales order');
    } finally {
      setLoading(false);
    }
  };

  // If no data, show loading
  if (!currentData) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading sales order data...</p>
        </div>
      </div>
    );
  }

  // If creation completed, show result
  if (creationStatus === 'success') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Sales Order Created!</h3>
          <p className="text-gray-600 mb-6">{creationMessage}</p>
          {netSuiteUrl && (
            <a
              href={netSuiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition mb-4"
            >
              View in NetSuite
            </a>
          )}
          <button
            onClick={onClose}
            className="block w-full mt-4 text-gray-500 hover:text-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // If processing (async creation), show processing message
  if (creationStatus === 'processing') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Creating Sales Order...</h3>
          <p className="text-gray-600 mb-4">{creationMessage}</p>
          <p className="text-sm text-gray-500">This window will close automatically.</p>
        </div>
      </div>
    );
  }

  // Get line items
  const lineItems = currentData.line_items || currentData.mapped_items || [];
  const currencySymbol = currentData.currency || workflowContext?.currency_symbol || '$';

  // Debug: Log line items to check for rdd field
  console.log('📦 [PreSalesOrderModal] Line items:', lineItems);
  console.log('📦 [PreSalesOrderModal] First item fields:', lineItems[0] ? Object.keys(lineItems[0]) : 'no items');
  if (lineItems[0]) {
    console.log('📦 [PreSalesOrderModal] First item rdd:', lineItems[0].rdd, 'delivery_date:', lineItems[0].delivery_date);
  }

  return (
    <div className={`fixed z-50 transition-all duration-300 ${
      isMinimized
        ? 'inset-auto bottom-0 right-0 p-0'  // No overlay when minimized - can see background
        : 'inset-0 bg-black/50 flex items-center justify-center p-4'  // Full overlay when maximized
    }`}>
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl transition-all duration-300 ${
        isMinimized
          ? 'fixed bottom-4 right-4 w-80 max-w-[90vw]'  // Small floating card when minimized
          : 'w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col'  // Full modal when maximized
      }`}>
        {/* Header */}
        <div className={`bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-3 flex items-center justify-between ${isMinimized ? 'rounded-t-xl' : ''}`}>
          <div className="flex items-center gap-3">
            {!isMinimized && (
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            )}
            <div>
              <h2 className={`font-bold text-white ${isMinimized ? 'text-sm' : 'text-xl'}`}>
                {isMinimized ? '📋 Sales Order Review' : 'Review Sales Order'}
              </h2>
              {!isMinimized && (
                <p className="text-white/80 text-sm">Confirm details before creating in NetSuite</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 hover:bg-white/20 rounded-lg transition"
              title={isMinimized ? 'Maximize' : 'Minimize'}
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMinimized ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                )}
              </svg>
            </button>
            <button
              onClick={onClose || onCancel}
              className="p-2 hover:bg-white/20 rounded-lg transition"
              title="Close"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Minimized status indicator */}
        {isMinimized && (
          <div className="p-3 bg-white dark:bg-gray-800 rounded-b-xl">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Click maximize to review sales order details
            </p>
          </div>
        )}

        {!isMinimized && (
          <>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Error message */}
              {creationStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-red-800">Creation Failed</p>
                    <p className="text-red-600 text-sm">{creationMessage}</p>
                  </div>
                </div>
              )}

              {/* Order Info Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Customer
                    {!currentData.customer_id && (
                      <span className="ml-2 text-yellow-600 text-sm">⚠️ Not matched</span>
                    )}
                  </h3>

                  {/* Show customer info if matched */}
                  {currentData.customer_id && selectedCustomer ? (
                    <div className="space-y-2">
                      <p className="text-lg font-medium text-gray-900">{currentData.customer_name || 'N/A'}</p>
                      {currentData.customer_entity_id && (
                        <p className="text-sm text-gray-500">ID: {currentData.customer_entity_id}</p>
                      )}
                      {currentData.customer_email && (
                        <p className="text-sm text-gray-500">{currentData.customer_email}</p>
                      )}
                    </div>
                  ) : (
                    /* Show dropdown with Load All when customer not matched */
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {currentData.customer_name ? `Customer "${currentData.customer_name}" not found - Select Manually:` : 'Select Customer:'}
                      </label>

                      {/* Search Input and Load All Customers Button */}
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          placeholder="🔍 Search customers..."
                          value={customerSearchTerm}
                          onChange={(e) => setCustomerSearchTerm(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-white text-gray-900"
                          disabled={!allCustomers.length}
                        />
                        <button
                          onClick={handleLoadAllCustomers}
                          disabled={loadingAllCustomers || allCustomers.length > 0}
                          className={`px-4 py-2 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                            allCustomers.length > 0
                              ? 'bg-green-600 text-white cursor-not-allowed'
                              : loadingAllCustomers
                              ? 'bg-gray-400 text-white cursor-wait'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                          title={allCustomers.length > 0 ? 'All customers loaded' : 'Load all NetSuite customers'}
                        >
                          {loadingAllCustomers ? (
                            <>⏳ Loading...</>
                          ) : allCustomers.length > 0 ? (
                            <>✅ All Loaded</>
                          ) : (
                            <>👥 Load All</>
                          )}
                        </button>
                      </div>

                      <select
                        value={selectedCustomer}
                        onChange={(e) => setSelectedCustomer(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900"
                      >
                        {(() => {
                          const customersSource = allCustomers.length > 0 ? allCustomers : (currentData?.available_customers || []);
                          const searchTerm = customerSearchTerm.toLowerCase();

                          const filteredCustomers = customersSource
                            .filter(customer => customer !== null && customer !== undefined)
                            .filter(customer => {
                              if (!searchTerm) return true;
                              const companyName = (customer?.company_name || customer?.companyname || '').toLowerCase();
                              const entityId = (customer?.entity_id || customer?.entityid || '').toLowerCase();
                              return companyName.includes(searchTerm) || entityId.includes(searchTerm);
                            });

                          const displayText = allCustomers.length > 0
                            ? `-- Select from ${customersSource.length} customers (All Loaded) --`
                            : customersSource.length > 0
                            ? `-- Select from ${customersSource.length} customers --`
                            : '-- Click "Load All" to load customers --';

                          return (
                            <>
                              <option value="">{displayText}</option>
                              {filteredCustomers.map(customer => (
                                <option
                                  key={customer?.id || customer?.customer_id}
                                  value={customer?.id || customer?.customer_id}
                                >
                                  {customer?.company_name || customer?.companyname} ({customer?.entity_id || customer?.entityid})
                                </option>
                              ))}
                            </>
                          );
                        })()}
                      </select>
                      {allCustomers.length > 0 && customerSearchTerm && (
                        <p className="text-xs text-gray-500 mt-1">
                          Showing filtered results from {allCustomers.length} total customers
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Order Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Order Details
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">PO Number:</span>
                      <p className="font-medium">{currentData.customer_po_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Order Date:</span>
                      <p className="font-medium">{currentData.sales_date || currentData.po_date || workflowContext?.sales_date || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Currency:</span>
                      <p className="font-medium">{currentData.currency || workflowContext?.currency || currencySymbol}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subsidiary, Location, Class Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Subsidiary */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subsidiary</label>
                  <p className="font-medium text-gray-900">
                    {workflowContext?.subsidiary_name || currentData?.subsidiary_name || 'Not selected'}
                  </p>
                </div>

                {/* Location */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                  <p className="font-medium text-gray-900">
                  {workflowContext?.location_name ||
                   workflowContext?.get_location_class_ai_step_data?.location_name ||
                   currentData?.location_name || 'N/A'}
                  {(workflowContext?.location_id || workflowContext?.get_location_class_ai_step_data?.location_id || currentData?.location_id) &&
                    <span className="text-gray-500 text-sm ml-2">
                      (ID: {workflowContext?.location_id || workflowContext?.get_location_class_ai_step_data?.location_id || currentData?.location_id})
                    </span>
                  }
                </p>
                </div>

                {/* Class */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Class</label>
                  <p className="font-medium text-gray-900">
                    {workflowContext?.class_name ||
                     workflowContext?.get_location_class_ai_step_data?.class_name ||
                     currentData?.class_name || 'N/A'}
                    {(workflowContext?.class_id || workflowContext?.get_location_class_ai_step_data?.class_id || currentData?.class_id) &&
                      <span className="text-gray-500 text-sm ml-2">
                        (ID: {workflowContext?.class_id || workflowContext?.get_location_class_ai_step_data?.class_id || currentData?.class_id})
                      </span>
                    }
                  </p>
                </div>
              </div>

              {/* Tax Code */}
              <div className="bg-green-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax Code</label>
                <p className="font-medium text-gray-900">
                  {workflowContext?.tax_code_name || workflowContext?.default_tax_code_name || currentData?.tax_label || currentData?.tax_code_name || 'N/A'}
                </p>
              </div>

              {/* AI Chat Section */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg overflow-hidden">
                {/* Chat Header - Always visible */}
                <button
                  onClick={() => setIsChatExpanded(!isChatExpanded)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-orange-100/50 transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900">AI Assistant</h3>
                      <p className="text-xs text-gray-500">Ask me to modify the sales order (change customer, items, quantities, etc.)</p>
                    </div>
                  </div>
                  <svg className={`w-5 h-5 text-gray-500 transition-transform ${isChatExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Chat Content - Expandable */}
                {isChatExpanded && (
                  <div className="border-t border-orange-200">
                    {/* Chat Messages */}
                    <div className="h-64 overflow-y-auto p-4 space-y-3 bg-white/50">
                      {chatMessages.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <p className="text-sm">Start a conversation to modify the sales order</p>
                          <p className="text-xs mt-1">Try: "Change the customer to Google" or "Update quantity on line 1 to 10"</p>
                        </div>
                      ) : (
                        chatMessages.map((msg, index) => (
                          <div
                            key={index}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                                msg.role === 'user'
                                  ? 'bg-orange-500 text-white'
                                  : msg.isError
                                  ? 'bg-red-100 text-red-800 border border-red-200'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-orange-200' : 'text-gray-400'}`}>
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                      {isChatLoading && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 rounded-lg px-4 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={chatMessagesEndRef} />
                    </div>

                    {/* Chat Input */}
                    <form onSubmit={handleChatSubmit} className="p-3 border-t border-orange-200 bg-white">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          placeholder="Type a message... (e.g., 'Change customer to Microsoft')"
                          disabled={isChatLoading}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                        />
                        <button
                          type="submit"
                          disabled={isChatLoading || !chatInput.trim()}
                          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isChatLoading ? (
                            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          )}
                          Send
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Line Items ({lineItems.length})
                  </h3>
                  <button
                    type="button"
                    onClick={handleLoadAllItems}
                    disabled={loadingAllItems || allItems.length > 0}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition flex items-center gap-1.5 ${
                      allItems.length > 0
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : loadingAllItems
                        ? 'bg-gray-100 text-gray-500 cursor-wait'
                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    }`}
                    title={allItems.length > 0 ? `${allItems.length} items loaded` : 'Load all NetSuite items for selection'}
                  >
                    {loadingAllItems ? (
                      <>
                        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </>
                    ) : allItems.length > 0 ? (
                      <>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {allItems.length} Items Loaded
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Load All Items
                      </>
                    )}
                  </button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Qty</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Unit Price</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Delivery Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">NetSuite Item</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {lineItems.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.description || item.item_description || item.po_item_description || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-center text-gray-900">
                            {item.quantity || item.po_quantity || 0}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900">
                            {formatCurrency(item.unit_price || item.po_unit_price, currencySymbol)}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                            {formatCurrency(item.amount || (item.quantity * item.unit_price), currencySymbol)}
                          </td>
                          <td className="px-4 py-3 text-sm text-center text-gray-900">
                            {item.rdd || item.RDD || item.delivery_date || item.deliveryDate || item.requested_delivery_date || item.requestedDeliveryDate || item.custcol_bbtrdd || item.expected_delivery_date || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {allItems.length > 0 ? (
                              <div className="relative">
                                <input
                                  type="text"
                                  placeholder="Search items..."
                                  value={itemSearchTerms[index] || ''}
                                  onChange={(e) => setItemSearchTerms(prev => ({ ...prev, [index]: e.target.value }))}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                                />
                                <select
                                  value={String(selectedItems[index] || item.netsuite_item_id || '')}
                                  onChange={(e) => {
                                    console.log(`📦 Item selected for line ${index}:`, e.target.value);
                                    setSelectedItems(prev => ({ ...prev, [index]: e.target.value }));
                                  }}
                                  className="mt-1 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white"
                                >
                                  <option value="">-- Select Item --</option>
                                  {(() => {
                                    const searchTerm = (itemSearchTerms[index] || '').toLowerCase();
                                    // Show all items - use search to filter if needed
                                    const filteredItems = searchTerm
                                      ? allItems.filter(i =>
                                          (i.itemid || i.name || '').toLowerCase().includes(searchTerm) ||
                                          (i.displayname || '').toLowerCase().includes(searchTerm) ||
                                          (i.description || '').toLowerCase().includes(searchTerm) ||
                                          String(i.id || '').includes(searchTerm)
                                        )
                                      : allItems; // Show ALL items
                                    return filteredItems.map(i => {
                                      const itemCode = i.itemid || i.name || i.displayname || `Item ${i.id}`;
                                      const desc = i.description ? ` - ${i.description.substring(0, 50)}${i.description.length > 50 ? '...' : ''}` : '';
                                      const itemType = i.itemtype ? ` [${i.itemtype}]` : '';
                                      return (
                                        <option key={String(i.id)} value={String(i.id)}>
                                          {itemCode}{desc}{itemType}
                                        </option>
                                      );
                                    });
                                  })()}
                                </select>
                                {itemSearchTerms[index] && (
                                  <span className="text-xs text-gray-500 mt-1 block">
                                    Showing filtered results
                                  </span>
                                )}
                              </div>
                            ) : item.netsuite_item_name || item.netsuite_item_id ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                {item.netsuite_item_name || `Item ${item.netsuite_item_id}`}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Unmatched - Load items to select
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="5" className="px-4 py-3 text-right font-semibold text-gray-900">
                          Total:
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-lg text-gray-900">
                          {formatCurrency(currentData.total_amount, currencySymbol)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Bill To and Ship To Addresses */}
              {(currentData.bill_to_address || currentData.sale_to_address || currentData.ship_to_address) && (() => {
                // Helper function to get address field with multiple possible names
                const getAddressField = (addr, ...keys) => {
                  if (!addr) return null;
                  for (const key of keys) {
                    if (addr[key]) return addr[key];
                  }
                  return null;
                };

                const billAddr = currentData.bill_to_address;
                const shipAddr = currentData.sale_to_address || currentData.ship_to_address;

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Bill To Address */}
                    {billAddr && (
                      <div className="bg-purple-50 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Bill To Address
                        </h3>
                        <p className="text-sm text-gray-600">
                          {getAddressField(billAddr, 'company_name', 'companyName', 'name') && (
                            <span className="font-medium">{getAddressField(billAddr, 'company_name', 'companyName', 'name')}<br /></span>
                          )}
                          {getAddressField(billAddr, 'address_line1', 'addressLine1', 'line1', 'address1') && (
                            <>{getAddressField(billAddr, 'address_line1', 'addressLine1', 'line1', 'address1')}<br /></>
                          )}
                          {getAddressField(billAddr, 'address_line2', 'addressLine2', 'line2', 'address2') && (
                            <>{getAddressField(billAddr, 'address_line2', 'addressLine2', 'line2', 'address2')}<br /></>
                          )}
                          {billAddr.city && <>{billAddr.city}, </>}
                          {billAddr.state && <>{billAddr.state} </>}
                          {getAddressField(billAddr, 'postal_code', 'postalCode', 'zip', 'zipCode', 'pincode') && (
                            <>{getAddressField(billAddr, 'postal_code', 'postalCode', 'zip', 'zipCode', 'pincode')}<br /></>
                          )}
                          {billAddr.country}
                        </p>
                      </div>
                    )}

                    {/* Ship To / Sale To Address */}
                    {shipAddr && (
                      <div className="bg-teal-50 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <svg className="w-5 h-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Ship To Address
                        </h3>
                        <p className="text-sm text-gray-600">
                          {getAddressField(shipAddr, 'company_name', 'companyName', 'name') && (
                            <span className="font-medium">{getAddressField(shipAddr, 'company_name', 'companyName', 'name')}<br /></span>
                          )}
                          {getAddressField(shipAddr, 'address_line1', 'addressLine1', 'line1', 'address1') && (
                            <>{getAddressField(shipAddr, 'address_line1', 'addressLine1', 'line1', 'address1')}<br /></>
                          )}
                          {getAddressField(shipAddr, 'address_line2', 'addressLine2', 'line2', 'address2') && (
                            <>{getAddressField(shipAddr, 'address_line2', 'addressLine2', 'line2', 'address2')}<br /></>
                          )}
                          {shipAddr.city && <>{shipAddr.city}, </>}
                          {shipAddr.state && <>{shipAddr.state} </>}
                          {getAddressField(shipAddr, 'postal_code', 'postalCode', 'zip', 'zipCode', 'pincode') && (
                            <>{getAddressField(shipAddr, 'postal_code', 'postalCode', 'zip', 'zipCode', 'pincode')}<br /></>
                          )}
                          {shipAddr.country}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between">
              <button
                onClick={onCancel || onClose}
                disabled={loading}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSalesOrder}
                disabled={loading || !selectedCustomer}
                className="px-8 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-amber-600 transition disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Sales Order
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PreSalesOrderCreationModal;
